import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import { sanitizeArticleHtml, htmlToText, isValidSlug, safeUrl } from "@/lib/sanitizeHtml";
import { articles as staticArticles } from "@/data/articles";

export interface CmsArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  body_html: string;
  featured_image_url: string | null;
  featured_image_alt: string;
  author_name: string;
  category: string;
  tags: string[];
  meta_title: string;
  meta_description: string;
  canonical_url: string | null;
  og_title: string;
  og_description: string;
  og_image_url: string | null;
  status: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export const ARTICLE_CATEGORIES = [
  "Dog Containment",
  "Deer & Garden",
  "Buying Guide",
  "Cost & Process",
] as const;

const SITE = "https://modernfenceinstall.com";

/** Slugs that already exist as hand-built Learn articles (or reserved paths). */
const reservedSlugs = () =>
  new Set<string>([...staticArticles.map((a) => a.slug), "index", "new", "preview", "admin"]);

const publicClient = () => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) {
          h.delete("Authorization");
        }
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
};

const PUBLIC_COLUMNS =
  "id, slug, title, excerpt, body_html, featured_image_url, featured_image_alt, author_name, category, tags, meta_title, meta_description, canonical_url, og_title, og_description, og_image_url, status, published_at, created_at, updated_at";

/* ------------------------------------------------------------------ public */

export const listPublishedCmsArticles = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await publicClient()
    .from("cms_articles")
    .select(PUBLIC_COLUMNS)
    .eq("status", "published")
    .order("published_at", { ascending: false });
  if (error) throw new Error("Published articles are temporarily unavailable.");
  return (data ?? []) as CmsArticle[];
});

export const getPublishedCmsArticle = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => ({ slug: String(data.slug).slice(0, 120) }))
  .handler(async ({ data }) => {
    if (!isValidSlug(data.slug)) return null;
    const { data: row, error } = await publicClient()
      .from("cms_articles")
      .select(PUBLIC_COLUMNS)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error("Article is temporarily unavailable.");
    return (row as CmsArticle | null) ?? null;
  });

/* --------------------------------------------------------------- publisher */

type Ctx = { supabase: ReturnType<typeof publicClient>; userId: string };

const assertPublisher = async (context: Ctx) => {
  const [pub, adm] = await Promise.all([
    context.supabase.rpc("has_role", { _user_id: context.userId, _role: "content_publisher" }),
    context.supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
  ]);
  if (pub.error || adm.error) throw new Error("Could not verify your permissions. Try again.");
  if (!pub.data && !adm.data) throw new Error("Forbidden: content publisher access required");
};

export const listPublisherArticles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertPublisher(context as unknown as Ctx);
    const { data, error } = await (context as unknown as Ctx).supabase
      .from("cms_articles")
      .select(PUBLIC_COLUMNS)
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as CmsArticle[];
  });

export const getPublisherArticle = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => ({ id: String(data.id) }))
  .handler(async ({ data, context }) => {
    await assertPublisher(context as unknown as Ctx);
    const { data: row, error } = await (context as unknown as Ctx).supabase
      .from("cms_articles")
      .select(PUBLIC_COLUMNS)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row as CmsArticle | null) ?? null;
  });

export interface SaveArticleInput {
  id?: string | null;
  slug: string;
  title: string;
  excerpt?: string;
  body_html?: string;
  featured_image_url?: string | null;
  featured_image_alt?: string;
  author_name?: string;
  category?: string;
  tags?: string[];
  meta_title?: string;
  meta_description?: string;
  canonical_url?: string | null;
  og_title?: string;
  og_description?: string;
  og_image_url?: string | null;
  status?: string;
  published_at?: string | null;
}

const text = (value: unknown, max: number) => String(value ?? "").trim().slice(0, max);

const imageUrl = (value: unknown): string | null => {
  const raw = text(value, 2000);
  if (!raw) return null;
  const url = safeUrl(raw);
  return url && /^(https?:\/\/|\/)/.test(url) ? url : null;
};

export const savePublisherArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: SaveArticleInput) => data)
  .handler(async ({ data, context }) => {
    const ctx = context as unknown as Ctx;
    await assertPublisher(ctx);

    const slug = text(data.slug, 96).toLowerCase();
    if (!isValidSlug(slug)) {
      throw new Error("Slug must be lowercase letters, numbers and hyphens (3–96 characters).");
    }
    if (reservedSlugs().has(slug)) {
      throw new Error(`The slug "${slug}" is already used by an existing article.`);
    }
    const title = text(data.title, 200);
    if (!title) throw new Error("Title is required.");

    const status = data.status === "published" ? "published" : "draft";
    const bodyHtml = sanitizeArticleHtml(String(data.body_html ?? ""));
    if (status === "published" && htmlToText(bodyHtml).length < 50) {
      throw new Error("Add article body content before publishing.");
    }

    const canonicalRaw = text(data.canonical_url, 500);
    const canonical = canonicalRaw ? safeUrl(canonicalRaw) : null;
    if (canonicalRaw && (!canonical || !/^https?:\/\//i.test(canonical))) {
      throw new Error("Canonical URL must be a full http(s) URL.");
    }

    const excerpt = text(data.excerpt, 400) || htmlToText(bodyHtml).slice(0, 200);

    const row = {
      slug,
      title,
      excerpt,
      body_html: bodyHtml,
      featured_image_url: imageUrl(data.featured_image_url),
      featured_image_alt: text(data.featured_image_alt, 300),
      author_name: text(data.author_name, 120) || "Modern Fence Install",
      category: (ARTICLE_CATEGORIES as readonly string[]).includes(text(data.category, 60))
        ? text(data.category, 60)
        : "Buying Guide",
      tags: (Array.isArray(data.tags) ? data.tags : [])
        .map((t) => text(t, 40))
        .filter(Boolean)
        .slice(0, 12),
      meta_title: text(data.meta_title, 120),
      meta_description: text(data.meta_description, 320),
      canonical_url: canonical ?? `${SITE}/learn/${slug}`,
      og_title: text(data.og_title, 160),
      og_description: text(data.og_description, 320),
      og_image_url: imageUrl(data.og_image_url),
      status,
      published_at:
        status === "published" ? (text(data.published_at, 40) || new Date().toISOString()) : null,
    };

    if (data.id) {
      const { data: updated, error } = await ctx.supabase
        .from("cms_articles")
        .update(row)
        .eq("id", data.id)
        .select(PUBLIC_COLUMNS)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!updated) throw new Error("Article not found.");
      return updated as CmsArticle;
    }

    const { data: inserted, error } = await ctx.supabase
      .from("cms_articles")
      .insert(row)
      .select(PUBLIC_COLUMNS)
      .single();
    if (error) {
      throw new Error(
        error.code === "23505" ? `The slug "${slug}" is already taken.` : error.message,
      );
    }
    return inserted as CmsArticle;
  });

export const deletePublisherArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => ({ id: String(data.id) }))
  .handler(async ({ data, context }) => {
    const ctx = context as unknown as Ctx;
    await assertPublisher(ctx);
    const { error } = await ctx.supabase.from("cms_articles").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
